<script setup lang="ts">
import type {
	DialogContentEmits,
	DialogContentProps as DialogRootContentProps
} from 'reka-ui';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';
import {
	DialogClose,
	DialogContent,
	DialogOverlay,
	DialogPortal,
	useForwardPropsEmits
} from 'reka-ui';
import { computed } from 'vue';

import CloseIcon from '~icons/material-symbols-light/close';

export type DialogContentProps = DialogRootContentProps & {
	class?: HTMLAttributes['class'];
	overlayClass?: HTMLAttributes['class'];
	showCloseIcon?: boolean;
	dataTestid?: string;
};

const props = withDefaults(defineProps<DialogContentProps>(), {
	showCloseIcon: true
});
const emits = defineEmits<DialogContentEmits>();

const delegatedProps = computed(() => {
	const { class: _, ...delegated } = props;

	return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
	<DialogPortal>
		<Transition
			enter-active-class="transition-opacity duration-200 ease-out"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-200 ease-in"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<DialogOverlay
				:class="cn('fixed inset-0 z-50 bg-black/40', props.overlayClass)"
			/>
		</Transition>
		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="opacity-0 scale-95"
			enter-to-class="opacity-100 scale-100"
			leave-active-class="transition-all duration-200 ease-in"
			leave-from-class="opacity-100 scale-100"
			leave-to-class="opacity-0 scale-95"
		>
			<DialogContent
				v-bind="forwarded"
				:data-test-id="props.dataTestid"
				:class="
					cn(
						'fixed left-1/2 top-1/2 z-51 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 bg-background shadow-lg sm:rounded-lg focus-visible:outline-none',
						props.class
					)
				"
				@close-auto-focus.prevent
			>
				<slot></slot>

				<DialogClose
					v-if="props.showCloseIcon"
					class="absolute right-4 top-5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
				>
					<CloseIcon class="size-8 cursor-pointer text-primary" />
					<span class="sr-only">Close</span>
				</DialogClose>
			</DialogContent>
		</Transition>
	</DialogPortal>
</template>
